import { useEffect, useRef } from 'react';

/**
 * Restores keyboard focus to an expand-to-dialog's trigger button when the
 * dialog closes.
 *
 * The capped multiline fields (`TextAreaField`, `AgentInstructionField`)
 * *unmount* their expand button while the dialog is open — the whole input
 * (button included) is removed so two live-synced `TokenizedInput`s never share
 * a value. That makes focus restoration from *inside* the dialog impossible:
 * anything the dialog captures as the trigger is a detached node by close time,
 * so `.focus()` on it silently no-ops and focus falls to `<body>`.
 *
 * The fix is to let the *owner* of the (remounting) button restore focus. Attach
 * the returned ref to the trigger button and pass the dialog's open state. When
 * `open` transitions true → false the button has just remounted (same commit),
 * so the ref points at the live element and this effect refocuses it.
 *
 * @param open Whether the expand dialog is currently open.
 * @returns A ref to attach to the trigger `Button`.
 */
export function useRestoreFocusOnDialogClose<T extends HTMLElement>(
  open: boolean
) {
  const triggerRef = useRef<T>(null);
  const wasOpen = useRef(false);
  useEffect(() => {
    if (wasOpen.current && !open) {
      triggerRef.current?.focus();
    }
    wasOpen.current = open;
  }, [open]);
  return triggerRef;
}
